<template>
  <div>
    <el-dialog
        :visible.sync="isDialogVisible"
        :before-close="closeDialog"
        :close-on-press-escape="false"
        :close-on-click-modal="false"
    >
      <div slot="title" class="el-dialog__title_border">
        <el-button v-if="canGoBack"
                   @click="back" class="u-mr3"
                   :icon="['far', 'chevron-left']"
                   type="text">
        </el-button>
        <div class="el-dialog__title">
          {{ t('payments.add_payment_method_title') }}
        </div>
      </div>
      <div>
        <div class="js-select-insured" v-if="workflow.page === 'select-insured'">
          <div class="u-mt2 u-mb4">{{t('payments.select_insured_for_payment_method')}}</div>
          <div class="js-single-insured u-mt2" v-for="ins in newPaymentMethodOwners" >
            <el-radio :label="ins" v-model="state.selectedInsured">
              {{ accountName(ins) }}
            </el-radio>
          </div>
        </div>
        <div v-if="workflow.page === 'add-card'" v-loading="state.loading">
          <r-ba-add-payment-method v-if="addPaymentMethodComponentToShow==='classic'"
                                   @paymentMethodAdded="closeDialog"
                                   :insured-id="state.selectedInsured"
                                   ref="addPayMethod"></r-ba-add-payment-method>
          <r-ba-add-payment-method-stripe v-if="addPaymentMethodComponentToShow==='stripe'"
                                   @paymentMethodAdded="closeDialog"
                                   :insured-id="state.selectedInsured"
                                   :stripeSetupData="stripeSetupData"
                                   ref="addPayMethod"></r-ba-add-payment-method-stripe>
          <div class="dialog-loading-placeholder" v-if="!addPaymentMethodComponentToShow"/>
        </div>



      </div>
      <div v-if="workflow.page !== 'success' || (workflow.page === 'success' && !state.loading)"
           slot="footer" class="u-flex-justify-content-end">
        <el-button v-loading="state.loading"  @click="next" :disabled="disabledNextStep" v-if="!canGoNext" type="primary" class="u-text-capitalize">
          {{ t('global.next') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>

import NewAddPaymentMethod from './payment-methods/r-add-payment-method.component'
import NewAddPaymentMethodStripe from './payment-methods/r-add-payment-method-stripe.component'
import {mapGetters, mapState, mapMutations, mapActions} from 'vuex'

const Step1SelectInsured = 'select-insured'
const Step2AddCard = 'add-card'

export default {
  name: 'ba-add-payment-method',
  props: ['availableInsureds'],
  components: {
    'r-ba-add-payment-method': NewAddPaymentMethod,
    'r-ba-add-payment-method-stripe': NewAddPaymentMethodStripe,
  },
  computed: {
    ...mapGetters('account', ['account']),
    ...mapState('common', ['contacts', 'dialogPayData']),
    newPaymentMethodOwners () {
      return this.dialogPayData.customInsureds || this.availableInsureds
    },
    isDialogVisible () {
      return this.dialogPayData.isDialogVisible
    },
    canGoBack () {
      if (this.workflow.page === Step1SelectInsured || this.newPaymentMethodOwners.length === 1) {
        return false
      }
      return true
    },
    disabledNextStep () {
      if (this.workflow.page === Step1SelectInsured && !this.state.selectedInsured) {
        return true
      }
      return false
    },
    canGoNext () {
      if (this.workflow.page === Step1SelectInsured) {
        return false
      }

      return true
    }
  },
  watch: {
    newPaymentMethodOwners: {
      handler (val, oldVal) {
        this.initDialogPageState()
      },
      immediate: true
    },
    isDialogVisible: {
      handler (val, oldVal) {
        this.initDialogPageState()
        if (val) this.makeRetrievePaymentMethodCreationDetailsCall()
      },
      immediate: true
    },
  },
  methods: {
    ...mapMutations('common', ['handlePayDialog']),
    ...mapActions('payments', ['addPaymentMethod', 'retrievePaymentMethodCreationDetails']),
    /*
      this is helper function to translate insured_id to account name
    */
    accountName (insuredId: string) {
      let ins = this.account.insureds[insuredId]
      if (!ins) {
        return insuredId
      }
      let contactId = ins.contact_id
      let result
      this.contacts.forEach(_ => {
        if (_.id === contactId) {
          result = _
        }
      })

      if (result) {
        return result.name
      }
      return insuredId
    },
    closeDialog () {
      return this.handlePayDialog({
        isDialogVisible: false,
        currentSubMenu: ''
      })
    },
    async initDialogPageState () {
      if (this.newPaymentMethodOwners.length > 1) {
        this.workflow.page = Step1SelectInsured
      } else {
        this.state.selectedInsured = this.newPaymentMethodOwners[0]
        this.workflow.page = Step2AddCard
        this.addPaymentMethodComponentToShow = null
      }
    },
    back () {
      this.workflow.page = Step1SelectInsured
    },
    next () {
      this.workflow.page = Step2AddCard
      this.addPaymentMethodComponentToShow = null
      this.makeRetrievePaymentMethodCreationDetailsCall()
    },
    async makeRetrievePaymentMethodCreationDetailsCall () {
      // call should be done only on "add card step" and only if insured was selected/determined
      if (this.workflow.page !== Step2AddCard) return
      if (!this.state.selectedInsured) return
      this.state.loading = true

      let paymentMethodCreationDetails
      try {
        paymentMethodCreationDetails = await this.retrievePaymentMethodCreationDetails(this.state.selectedInsured)
        if (!paymentMethodCreationDetails.success) {
          this.$elAlert('Failed during payment method processor determination.', this.t('global.notice'))
          return
        }
      } catch (e) {
        this.$elAlert('Failed during payment method processor determination.', this.t('global.notice'))
        return
      } finally {
        this.state.loading = false
      }

      let paymentMethodCreationDetailsData = paymentMethodCreationDetails.data
      let requestPaymentMethodEntryResponse = paymentMethodCreationDetailsData.request_payment_method_entry_response
      if (requestPaymentMethodEntryResponse && requestPaymentMethodEntryResponse.embed_stripe_setup_intent) {
        this.stripeSetupData = requestPaymentMethodEntryResponse.embed_stripe_setup_intent
        this.addPaymentMethodComponentToShow = 'stripe'
      } else this.addPaymentMethodComponentToShow = 'classic'
    }
  },
  mounted () {
    this.initDialogPageState()
  },
  data () {
    return {
      workflow: {
        // select-insured -> add-card
        page: Step1SelectInsured,
      },
      state: {
        selectedInsured: null,
        loading: false,
      },
      addPaymentMethodComponentToShow: null,
      stripeSetupData: null
    }
  },
}
</script>

<style lang="scss">
  .dialog-loading-placeholder {
    height: 300px
  }
</style>
